import { useCallback, useRef, useState } from 'react';

export interface SubmitGuardModel {
	/** True while the guarded action is in flight — drive the button's busy state from this. */
	submitting: boolean;
	/**
	 * Run `action` unless one is already running. Returns the action's result,
	 * or undefined when the call was refused because another was in flight.
	 */
	guard: <T>(action: () => Promise<T> | T) => Promise<T | undefined>;
}

/**
 * PD-15267 — one in-flight submission at a time, for any button that sends
 * something to the server.
 *
 * Written after a driver on a weak cellular connection tapped Confirm
 * Quantities repeatedly — reasonably, because the screen showed no sign the
 * first tap had registered — and each tap issued its own request. Production
 * recorded nine identical submissions for one stop, three inside the same
 * second, which left duplicate delivered-line rows.
 *
 * The ref is the actual guard and `submitting` exists only to render the busy
 * state. That split is the point: React does not apply new state until the
 * next render, so two taps landing in the same frame would BOTH pass a
 * state-only check, whereas the ref is set synchronously before the first
 * await. A state-only guard looks correct and fails exactly when a user is
 * tapping fast, which is precisely when it is needed.
 *
 * The lock releases in a `finally`, so a failed submission can be retried
 * deliberately.
 *
 * This guards ONE client. It cannot prevent a duplicate arriving from a retry,
 * a refreshed page or a second device, so an endpoint whose repetition is
 * harmful still needs its own server-side protection (a uniqueness rule, a
 * lock, or an idempotency key).
 *
 * ```tsx
 * const { submitting, guard } = useSubmitGuard();
 *
 * <Button
 *   onClick={() => void guard(() => save(form))}
 *   disabled={submitting}
 *   loading={submitting}
 *   loadingLabel={t('Saving — please wait…')}
 * >
 *   {t('Save')}
 * </Button>
 * ```
 */
export function useSubmitGuard(): SubmitGuardModel {
	const lockRef = useRef(false);
	const [submitting, setSubmitting] = useState(false);

	const guard = useCallback(
		async <T>(action: () => Promise<T> | T): Promise<T | undefined> => {
			if (lockRef.current) {
				return undefined;
			}

			lockRef.current = true;
			setSubmitting(true);

			try {
				return await action();
			} finally {
				lockRef.current = false;
				setSubmitting(false);
			}
		},
		[],
	);

	return { submitting, guard };
}
